export function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

function mean(values) {
  return values.reduce((sum, value) => sum + value, 0) / Math.max(values.length, 1)
}

export function isOpenPalm(landmarks) {
  if (!landmarks || landmarks.length < 21) return false
  const wrist = landmarks[0]
  const extended = (tip, pip, margin = 1.08) => distance(landmarks[tip], wrist) > distance(landmarks[pip], wrist) * margin
  const fourFingersOpen = [[8, 6], [12, 10], [16, 14], [20, 18]]
    .every(([tip, pip]) => extended(tip, pip, 1.12))
  const palmWidth = distance(landmarks[5], landmarks[17])
  const thumbSpread = distance(landmarks[4], landmarks[5]) > Math.max(0.06, palmWidth * 0.72)
  return fourFingersOpen && thumbSpread
}

export function isFist(landmarks) {
  if (!landmarks || landmarks.length < 21) return false
  const wrist = landmarks[0]
  return [[5, 6, 8], [9, 10, 12], [13, 14, 16], [17, 18, 20]]
    .every(([mcp, pip, tip]) => {
      const curledTowardPalm = distance(landmarks[tip], landmarks[mcp]) < distance(landmarks[pip], landmarks[mcp]) * 1.65
      const foldedTowardWrist = distance(landmarks[tip], wrist) < distance(landmarks[pip], wrist) * 1.18
      return curledTowardPalm || foldedTowardWrist
    })
}

export function isPointingHand(landmarks) {
  if (!landmarks || landmarks.length < 21) return false
  const wrist = landmarks[0]
  const extended = (tip, pip, margin = 1.08) => distance(landmarks[tip], wrist) > distance(landmarks[pip], wrist) * margin
  const indexUp = extended(8, 6, 1.13)
  const foldedCount = [[12, 10], [16, 14], [20, 18]]
    .filter(([tip, pip]) => !extended(tip, pip, 1.05)).length
  return indexUp && foldedCount >= 2
}

export function smoothPoint(previous, next, alpha = 0.46) {
  if (!previous) return { ...next }
  return {
    x: previous.x + (next.x - previous.x) * alpha,
    y: previous.y + (next.y - previous.y) * alpha,
  }
}

export function signedArea(points) {
  if (points.length < 3) return 0
  let area = 0
  for (let i = 0; i < points.length; i += 1) {
    const next = points[(i + 1) % points.length]
    area += points[i].x * next.y - next.x * points[i].y
  }
  return area / 2
}

export function evaluateCircle(points, viewportWidth, viewportHeight) {
  const result = {
    ok: false,
    score: 0,
    reason: '軌跡還不夠長',
    center: null,
    radius: 0,
    closure: 1,
    roundness: 1,
    coverage: 0,
  }
  if (!points || points.length < 24) return result

  const xs = points.map((point) => point.x)
  const ys = points.map((point) => point.y)
  const minX = Math.min(...xs); const maxX = Math.max(...xs)
  const minY = Math.min(...ys); const maxY = Math.max(...ys)
  const width = maxX - minX; const height = maxY - minY
  const minRequired = Math.min(viewportWidth, viewportHeight) * 0.2
  if (Math.min(width, height) < minRequired) {
    return { ...result, reason: '魔法陣再畫大一點' }
  }

  const center = { x: mean(xs), y: mean(ys) }
  const radii = points.map((point) => distance(point, center))
  const radius = mean(radii)
  const radialDeviation = Math.sqrt(mean(radii.map((value) => (value - radius) ** 2))) / Math.max(radius, 1)
  const diagonal = Math.hypot(width, height)
  const closure = distance(points[0], points.at(-1)) / Math.max(diagonal, 1)
  let pathLength = 0
  for (let i = 1; i < points.length; i += 1) pathLength += distance(points[i - 1], points[i])
  const circumference = Math.PI * 2 * radius
  const coverage = pathLength / Math.max(circumference, 1)
  const aspectPenalty = Math.abs(width / Math.max(height, 1) - 1)

  const closureScore = Math.max(0, 1 - closure / 0.42)
  const roundnessScore = Math.max(0, 1 - radialDeviation / 0.48)
  const aspectScore = Math.max(0, 1 - aspectPenalty / 0.85)
  const coverageScore = Math.max(0, 1 - Math.abs(1 - coverage) / 0.85)
  const score = closureScore * 0.36 + roundnessScore * 0.3 + aspectScore * 0.18 + coverageScore * 0.16

  let reason = '把圓收回起點'
  if (closure <= 0.4 && radialDeviation > 0.42) reason = '放慢一點，讓軌跡更圓'
  if (closure <= 0.4 && radialDeviation <= 0.42 && coverage < 0.62) reason = '再多畫一點，讓圓完整'
  const ok = closure <= 0.4 && radialDeviation <= 0.42 && aspectPenalty <= 0.72 && coverage >= 0.62 && coverage <= 1.95
  if (ok) reason = '魔法陣成立'

  return {
    ok,
    score: Math.round(score * 100),
    reason,
    center,
    radius,
    closure,
    roundness: radialDeviation,
    coverage,
    clockwise: signedArea(points) > 0,
  }
}

export function makeCirclePoints(cx, cy, radius, count = 72, noise = 0) {
  return Array.from({ length: count }, (_, index) => {
    const angle = (index / (count - 1)) * Math.PI * 2
    const wobble = Math.sin(index * 1.71) * noise
    return {
      x: cx + Math.cos(angle) * (radius + wobble),
      y: cy + Math.sin(angle) * (radius + wobble),
    }
  })
}
